import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import Sidebar from '../components/Sidebar';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';
import { extractErrorMessage } from '../lib/errorUtils';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Upload, FileText, CheckCircle2, AlertCircle } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Map common column names to Lead fields (case-insensitive)
const FIELD_MAP = {
  first_name: ['first_name', 'firstname', 'nome', 'name', 'first name'],
  last_name: ['last_name', 'lastname', 'cognome', 'surname', 'last name'],
  full_name: ['full_name', 'nome completo', 'name complete'],
  phone: ['phone', 'telefono', 'tel', 'numero di telefono', 'phone number', 'numero'],
  secondary_phone: ['secondary_phone', 'tel_secondario', 'tel. secondario', 'numero di telefono secondario', 'phone2'],
  whatsapp_number: ['whatsapp', 'whatsapp_number', 'numero whatsapp', 'wa'],
  email: ['email', 'e-mail', 'indirizzo e-mail', 'mail', 'email address'],
  origin: ['origin', 'origine', 'source'],
  form_module: ['form', 'modulo', 'module', 'form_module'],
  channel: ['channel', 'canale'],
  stage: ['stage', 'fase', 'phase'],
  labels: ['labels', 'etichette', 'tags', 'label', 'tag'],
  country: ['country', 'paese'],
  language: ['language', 'lingua'],
  facebook_notes: ['notes', 'note', 'notas', 'nota', 'facebook_notes'],
  created_at: ['data creazione', 'created', 'created_at', 'date created', 'date'],
};

const findField = (row, targetField) => {
  const aliases = FIELD_MAP[targetField] || [];
  const keys = Object.keys(row);
  for (const key of keys) {
    const normalized = key.toLowerCase().trim();
    if (aliases.includes(normalized)) {
      return row[key];
    }
  }
  return null;
};

const normalizeLead = (row, campaignId) => {
  const fullName = findField(row, 'full_name');
  let firstName = findField(row, 'first_name');
  let lastName = findField(row, 'last_name');

  if (!firstName && fullName) {
    const parts = String(fullName).trim().split(/\s+/);
    firstName = parts[0] || '';
    lastName = parts.slice(1).join(' ') || '';
  }
  if (!firstName) firstName = 'Unknown';
  if (!lastName) lastName = '';

  const phone = String(findField(row, 'phone') || '').trim();
  const labels = findField(row, 'labels');
  let labelsArray = [];
  if (labels) {
    labelsArray = String(labels)
      .split(/[,;|]/)
      .map((l) => l.trim())
      .filter(Boolean);
  }

  return {
    first_name: String(firstName).trim(),
    last_name: String(lastName).trim(),
    phone,
    email: findField(row, 'email') || null,
    campaign_id: campaignId,
    origin: findField(row, 'origin') || 'A pagamento',
    form_module: findField(row, 'form_module') || null,
    channel: findField(row, 'channel') || 'E-mail',
    stage: findField(row, 'stage') || 'Acquisiti',
    labels: labelsArray,
    secondary_phone: findField(row, 'secondary_phone') || null,
    whatsapp_number: findField(row, 'whatsapp_number') || null,
    country: findField(row, 'country') || null,
    language: findField(row, 'language') || null,
    facebook_notes: findField(row, 'facebook_notes') || null,
    custom_fields: {},
  };
};

const ImportLeads = ({ user, onLogout, theme, onToggleTheme }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [file, setFile] = useState(null);
  const [parsedRows, setParsedRows] = useState([]);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const { t } = useLanguage();
  const navigate = useNavigate();

  useEffect(() => {
    if (user.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchCampaigns();
  }, [user, navigate]);

  const fetchCampaigns = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/campaigns`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCampaigns(response.data);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    }
  };

  const parseCSV = (fileObj) => {
    Papa.parse(fileObj, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        setParsedRows(results.data);
        toast.success(`${results.data.length} righe rilevate nel CSV`);
      },
      error: (err) => {
        toast.error(`Errore parsing CSV: ${err.message}`);
      },
    });
  };

  const parseExcel = (fileObj) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheet = workbook.SheetNames[0];
        const rows = XLSX.utils.sheet_to_json(workbook.Sheets[firstSheet], {
          defval: '',
          raw: false,
        });
        setParsedRows(rows);
        toast.success(`${rows.length} righe rilevate nel file Excel`);
      } catch (err) {
        toast.error(`Errore parsing Excel: ${err.message}`);
      }
    };
    reader.readAsArrayBuffer(fileObj);
  };

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    setResult(null);
    const name = f.name.toLowerCase();
    if (name.endsWith('.csv')) {
      parseCSV(f);
    } else if (name.endsWith('.xlsx') || name.endsWith('.xls')) {
      parseExcel(f);
    } else {
      toast.error('Formato non supportato. Usa .csv, .xlsx o .xls');
      setFile(null);
    }
  };

  const handleImport = async () => {
    if (!selectedCampaign) {
      toast.error('Seleziona una campagna');
      return;
    }
    if (!parsedRows.length) {
      toast.error('Nessun dato da importare');
      return;
    }

    setImporting(true);
    setResult(null);

    try {
      const leads = parsedRows
        .map((row) => normalizeLead(row, selectedCampaign))
        .filter((l) => l.phone && l.phone.length > 0);

      if (!leads.length) {
        toast.error('Nessuna lead valida trovata (telefono mancante)');
        setImporting(false);
        return;
      }

      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/leads/import`, leads, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setResult(response.data);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Errore durante l\'importazione'));
    } finally {
      setImporting(false);
    }
  };

  const previewRows = parsedRows.slice(0, 5);
  const previewColumns = previewRows[0] ? Object.keys(previewRows[0]) : [];

  return (
    <div className="flex h-screen overflow-hidden">
      <Sidebar
        user={user}
        onLogout={onLogout}
        theme={theme}
        onToggleTheme={onToggleTheme}
        collapsed={collapsed}
        onToggle={() => setCollapsed(!collapsed)}
      />
      <div
        className="flex-1 overflow-y-auto"
        style={{ backgroundColor: theme === 'dark' ? '#0A0A0A' : '#F8F9FA' }}
      >
        <div className="p-6 lg:p-8 max-w-6xl">
          <div className="mb-8">
            <h1
              className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-2"
              style={{ color: theme === 'dark' ? '#F4F4F4' : '#161616' }}
              data-testid="import-title"
            >
              {t('import_title')}
            </h1>
            <p className="text-base" style={{ color: theme === 'dark' ? '#A8A8A8' : '#525252' }}>
              {t('import_subtitle')}
            </p>
          </div>

          <div
            className="p-6 rounded-lg border mb-6"
            style={{
              backgroundColor: theme === 'dark' ? '#161616' : '#FFFFFF',
              borderColor: theme === 'dark' ? '#262626' : '#E0E0E0',
            }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label className="mb-2 block">
                  {t('import_step1')}
                </Label>
                <Select value={selectedCampaign} onValueChange={setSelectedCampaign}>
                  <SelectTrigger data-testid="import-campaign-select">
                    <SelectValue placeholder={t('import_choose_campaign')} />
                  </SelectTrigger>
                  <SelectContent>
                    {campaigns.map((camp) => (
                      <SelectItem key={camp.id} value={camp.id}>
                        {camp.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="mb-2 block">
                  {t('import_step2')}
                </Label>
                <div className="relative">
                  <input
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleFileChange}
                    className="block w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-600 file:text-white hover:file:bg-blue-700 cursor-pointer"
                    style={{ color: theme === 'dark' ? '#A8A8A8' : '#525252' }}
                    data-testid="import-file-input"
                  />
                </div>
                {file && (
                  <p className="text-xs mt-2 flex items-center gap-1" style={{ color: '#0F62FE' }}>
                    <FileText size={14} />
                    {file.name} ({(file.size / 1024).toFixed(1)} KB)
                  </p>
                )}
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <Button
                className="btn-primary"
                onClick={handleImport}
                disabled={!file || !selectedCampaign || importing || !parsedRows.length}
                data-testid="import-submit-button"
              >
                <Upload size={18} className="mr-2" />
                {importing ? t('import_importing') : `${t('import_button')} ${parsedRows.length} ${t('import_leads_count')}`}
              </Button>
            </div>
          </div>

          {parsedRows.length > 0 && (
            <div
              className="p-6 rounded-lg border mb-6"
              style={{
                backgroundColor: theme === 'dark' ? '#161616' : '#FFFFFF',
                borderColor: theme === 'dark' ? '#262626' : '#E0E0E0',
              }}
            >
              <h3
                className="text-lg font-bold mb-4"
                style={{ color: theme === 'dark' ? '#F4F4F4' : '#161616' }}
              >
                {t('import_preview')} ({parsedRows.length} {t('import_preview_rows')})
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="import-preview-table">
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${theme === 'dark' ? '#262626' : '#E0E0E0'}` }}>
                      {previewColumns.map((col) => (
                        <th
                          key={col}
                          className="px-2 py-2 text-left uppercase tracking-wider whitespace-nowrap"
                          style={{ color: theme === 'dark' ? '#A8A8A8' : '#525252' }}
                        >
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.map((row, idx) => (
                      <tr
                        key={`row-${idx}-${row[previewColumns[0]] ?? ''}`}
                        style={{ borderBottom: `1px solid ${theme === 'dark' ? '#262626' : '#E0E0E0'}` }}
                      >
                        {previewColumns.map((col) => (
                          <td
                            key={col}
                            className="px-2 py-2 whitespace-nowrap"
                            style={{ color: theme === 'dark' ? '#F4F4F4' : '#161616' }}
                          >
                            {String(row[col] || '-').substring(0, 40)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {result && (
            <div
              className="p-6 rounded-lg border"
              style={{
                backgroundColor: theme === 'dark' ? '#161616' : '#FFFFFF',
                borderColor: '#198038',
              }}
              data-testid="import-result"
            >
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle2 size={24} style={{ color: '#198038' }} />
                <h3 className="text-lg font-bold" style={{ color: '#198038' }}>
                  {t('import_completed')}
                </h3>
              </div>
              <p style={{ color: theme === 'dark' ? '#F4F4F4' : '#161616' }}>{result.message}</p>
              {result.assignments && Object.keys(result.assignments).length > 0 && (
                <div className="mt-4">
                  <p className="text-sm font-medium mb-2" style={{ color: theme === 'dark' ? '#A8A8A8' : '#525252' }}>
                    {t('import_distribution')}
                  </p>
                  <div className="space-y-1">
                    {Object.entries(result.assignments).map(([name, count]) => (
                      <div
                        key={name}
                        className="flex justify-between text-sm p-2 rounded"
                        style={{ backgroundColor: theme === 'dark' ? '#0A0A0A' : '#F8F9FA' }}
                      >
                        <span style={{ color: theme === 'dark' ? '#F4F4F4' : '#161616' }}>{name}</span>
                        <span className="font-mono font-bold" style={{ color: '#0F62FE' }}>
                          {count} {t('import_leads_count')}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <Button
                className="mt-4"
                variant="outline"
                onClick={() => navigate('/leads')}
                data-testid="import-view-leads-button"
              >
                {t('import_view_leads')}
              </Button>
            </div>
          )}

          <div
            className="mt-6 p-4 rounded-lg text-xs"
            style={{
              backgroundColor: theme === 'dark' ? '#161616' : '#E8EAED',
              color: theme === 'dark' ? '#A8A8A8' : '#525252',
            }}
          >
            <div className="flex items-start gap-2">
              <AlertCircle size={16} style={{ color: '#0F62FE', flexShrink: 0, marginTop: 2 }} />
              <div>
                <p className="font-medium mb-1" style={{ color: theme === 'dark' ? '#F4F4F4' : '#161616' }}>
                  {t('import_columns_info')}
                </p>
                <p>
                  {t('import_columns_desc')}
                </p>
                <p className="mt-2">
                  {t('import_auto_assign_note')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportLeads;
